import { useState, useMemo, useRef } from 'react';
import isEqual from 'lodash/isEqual';
import throttle from 'lodash/throttle';
import { useResizeObserver } from '../useResizeObserver';
import { useLayoutEffectWithoutSSRComplaining } from '../useLayoutEffectWithoutSSRComplaining';

export type Size = {
  width?: number;
  height?: number;
};

export type Sizes = {
  contentSize?: Size;
  containerSize?: Size;
};

function getStyleValue(
  style: CSSStyleDeclaration,
  key: keyof CSSStyleDeclaration,
  defaultValue = 0
) {
  const value = style[key];
  if (value === null || value === undefined) {
    return defaultValue;
  }

  return parseInt(value as string, 10) || defaultValue;
}

// this measures the _available space_ for our happy little node
// so it can shrink or expand as needed
// note that this requires a non-statically positioned parent
function measureNode(node: HTMLElement): Sizes {
  const containerNode = node.parentElement;

  if (!containerNode || typeof window === 'undefined') {
    return {};
  }

  const containerWidth = containerNode.offsetWidth || 0;
  const containerHeight = containerNode.offsetHeight || 0;

  const style = window.getComputedStyle(containerNode);
  const paddingLeft = getStyleValue(style, 'paddingLeft');
  const paddingRight = getStyleValue(style, 'paddingRight');
  const paddingTop = getStyleValue(style, 'paddingTop');
  const paddingBottom = getStyleValue(style, 'paddingBottom');
  return {
    contentSize: {
      width: containerWidth - paddingLeft - paddingRight,
      height: containerHeight - paddingTop - paddingBottom,
    },
    containerSize: {
      width: containerWidth,
      height: containerHeight,
    },
  };
}

export function useAvailableSpace(
  ref: React.MutableRefObject<HTMLElement> | React.RefObject<HTMLElement | null>,
  onResize: (sizes: Sizes) => void,
  ssrSizes = {},
  throttleTimeout = 300
) {
  const prevSizes = useRef<Sizes>(ssrSizes); // for equality check without triggering a re-creation of the throttle on every update
  const [sizes, setSizes] = useState<Sizes>(ssrSizes); // for actually triggering a re-render when needed
  const measure = useMemo(
    () =>
      throttle(
        () => {
          if (!ref.current) {
            return;
          }

          const newSizes = measureNode(ref.current);

          if (isEqual(newSizes, prevSizes.current)) {
            return;
          }

          setSizes(newSizes);
          onResize(newSizes);
          prevSizes.current = newSizes;
        },
        throttleTimeout,
        { trailing: true }
      ),
    [onResize, throttleTimeout, ref]
  );
  useResizeObserver(ref, measure);

  useLayoutEffectWithoutSSRComplaining(() => {
    measure();
  }, [measure]);

  return sizes;
}
